import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { AlertColor, SelectChangeEvent } from '@mui/material';
import { DataGrid, GridColDef, GridColumnVisibilityModel, GridSortModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import UserSearchWidget from '~/components/Common/UserSearchWidget';
import {
  PROMO_RECENT_VISITS_UPDATED_EVENT,
  getPromoRecentVisits,
  recordVoucherVisit,
} from '~/components/Promo/PromoRecentVisits';
import ColumnSelector from '~/components/Promo/PromoTester/ColumnSelector';

import { usePromoPreferences } from '~/hooks/usePromoPreferences';

import { Voucher, VoucherToggle, VoucherWarning, getVoucherWarnings } from '~/services/PromoService';

import DebugModal from '../DebugModal/DebugModal';

import ReportingTag from './ReportingTag';
import {
  CASE_ID_FILTER_PRESET_OPTIONS,
  caseIdFilterOptionLabel,
  normalizeCaseIdFilterToken,
} from './caseIdFilterTokens';
import { useVoucherColumns } from './useVoucherColumns';
import {
  VOUCHER_LIST_API_SORT_BY_TO_FIELD,
  VOUCHER_LIST_SORT_FIELD_TO_API_SORT_BY,
  VoucherListSortBy,
  VoucherListSortDirection,
} from './voucherListSorting';

type Props = {
  voucherName: string;
  setVoucherName: (v: string) => void;
  reportingTagOptions: Array<{ value: string; label: string }>;
  selectedReportingTags: Array<string>;
  setSelectedReportingTags: (v: Array<string>) => void;
  excludedReportingTags: Array<string>;
  setExcludedReportingTags: (v: Array<string>) => void;
  adminUserId: string;
  setAdminUserId: (v: string) => void;
  adminUserSearchValue: string;
  setAdminUserSearchValue: React.Dispatch<React.SetStateAction<string>>;
  sortBy?: VoucherListSortBy;
  sortByDirection?: VoucherListSortDirection;
  onSortChange: (sortBy?: VoucherListSortBy, sortByDirection?: VoucherListSortDirection) => void;
  caseIdFilter: Array<string>;
  setCaseIdFilter: (v: Array<string>) => void;
  /** Shown on the switch; false while a case ID filter is applied. */
  effectiveOnlyActive: boolean;
  caseIdFilterActive: boolean;
  setOnlyActive: (v: boolean) => void;
  onClearAllFilters: () => void;
  data: Array<Voucher>;
  isLoading: boolean;
  totalCount: number;
  pageNum: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  onConfirmDelete: (voucher: Voucher) => Promise<void>;
  isDeleting: boolean;
  auditUsersById: Map<string, App.UserSummary>;
  selectedVoucherToggle?: VoucherToggle;
  onVoucherToggleClick: (toggle: VoucherToggle) => void;
  onClearVoucherToggle: () => void;
};

function getVoucherWarningSeverity(level?: string): AlertColor {
  if (level === 'error' || level === 'warning' || level === 'info' || level === 'success') {
    return level;
  }
  return 'warning';
}

const VOUCHER_WARNING_SEVERITIES: Array<AlertColor> = ['error', 'warning', 'info', 'success'];

function getVoucherWarningSeverityCounts(
  warnings: Array<VoucherWarning>,
): Array<{ severity: AlertColor; count: number }> {
  return VOUCHER_WARNING_SEVERITIES.flatMap((severity) => {
    const count = warnings.filter((warning) => getVoucherWarningSeverity(warning.level) === severity).length;
    return count > 0 ? [{ severity, count }] : [];
  });
}

function getVoucherWarningSeverityLabel(severity: AlertColor): string {
  return `${severity.slice(0, 1).toUpperCase()}${severity.slice(1)}`;
}

function getColumns({ onOpenWarnings }: { onOpenWarnings: (row: Voucher, severity: AlertColor) => void }) {
  const columns: Array<GridColDef<Voucher>> = [
    {
      field: 'actions',
      headerName: 'Actions',
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 0.5,
      hideable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to={`/marketing/vouchers/${params.row.voucherId}`}
            onClick={() =>
              recordVoucherVisit({
                code: params.row.voucherName,
                href: `/marketing/vouchers/${params.row.voucherId}`,
              })
            }
          >
            View
          </Button>
        </Stack>
      ),
    },
    {
      field: 'warnings',
      headerName: 'Warnings',
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 0.5,
      renderCell: (params) => {
        const severityCounts = getVoucherWarningSeverityCounts(getVoucherWarnings(params.row));

        if (severityCounts.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }

        return (
          <Stack spacing={0.5} alignItems="flex-start" sx={{ py: 0.5 }}>
            {severityCounts.map(({ severity, count }) => (
              <Chip
                key={severity}
                size="small"
                variant="outlined"
                color={severity}
                onClick={() => onOpenWarnings(params.row, severity)}
                label={`${getVoucherWarningSeverityLabel(severity)}: ${count}`}
              />
            ))}
          </Stack>
        );
      },
    },
    {
      field: 'debug',
      headerName: 'Debug',
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 0.5,
      renderCell: (params) => <DebugModal type="generic" title="Debug" data={params.row} />,
    },
  ];
  return columns;
}

export default function VouchersTab({
  voucherName,
  setVoucherName,
  reportingTagOptions,
  selectedReportingTags,
  setSelectedReportingTags,
  excludedReportingTags,
  setExcludedReportingTags,
  adminUserId,
  setAdminUserId,
  adminUserSearchValue,
  setAdminUserSearchValue,
  sortBy,
  sortByDirection,
  onSortChange,
  caseIdFilter,
  setCaseIdFilter,
  effectiveOnlyActive,
  caseIdFilterActive,
  setOnlyActive,
  onClearAllFilters,
  data,
  isLoading,
  totalCount,
  pageNum,
  pageSize,
  onPageChange,
  onConfirmDelete,
  isDeleting,
  auditUsersById,
  selectedVoucherToggle,
  onVoucherToggleClick,
  onClearVoucherToggle,
}: Props) {
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<Voucher | undefined>(undefined);
  const [warningsDialog, setWarningsDialog] = useState<{ voucher: Voucher; severity: AlertColor } | undefined>(
    undefined,
  );
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [voucherToClone, setVoucherToClone] = useState<Voucher | undefined>(undefined);
  /** Pending text in the Case ID Autocomplete (freeSolo values only commit on Enter unless we flush on blur). */
  const [caseIdFilterInput, setCaseIdFilterInput] = useState('');
  const [showMoreFilters, setShowMoreFilters] = useState(
    selectedReportingTags.length > 0 ||
      excludedReportingTags.length > 0 ||
      Boolean(adminUserId) ||
      caseIdFilter.length > 0,
  );
  const caseIdFilterRef = useRef(caseIdFilter);
  caseIdFilterRef.current = caseIdFilter;

  const rows = useMemo(() => data.map((row) => ({ id: row.voucherId, ...row })), [data]);
  const [promoPreferences, setPromoPreferences] = usePromoPreferences();
  const voucherListPreferences = promoPreferences.uiSelection.voucherList;
  const defaultColumnVisibilityModel = useMemo<GridColumnVisibilityModel>(
    () => ({
      voucherId: false,
      voucherTitle: false,
      parentVoucherId: false,
      voucherExpiry: false,
      voucherToggles: false,
      theme: false,
      productType: false,
      caseId: false,
      createdBy: false,
      createdAt: false,
      debug: false,
    }),
    [],
  );
  const reportingTagFilterOptions = useMemo(() => {
    const options = [...reportingTagOptions];
    [...selectedReportingTags, ...excludedReportingTags].forEach((tag) => {
      if (!options.some((option) => option.value === tag)) {
        options.push({ value: tag, label: tag });
      }
    });
    return options;
  }, [reportingTagOptions, selectedReportingTags, excludedReportingTags]);
  const reportingTagLabel = useCallback(
    (tag: string) => reportingTagFilterOptions.find((option) => option.value === tag)?.label ?? tag,
    [reportingTagFilterOptions],
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(() => ({
    ...defaultColumnVisibilityModel,
    ...voucherListPreferences.columnVisibility,
  }));
  const hasRestoredSort = useRef(false);
  const sortModel = useMemo<GridSortModel>(() => {
    if (!sortBy) {
      return [];
    }

    return [{ field: VOUCHER_LIST_API_SORT_BY_TO_FIELD[sortBy], sort: sortByDirection ?? 'asc' }];
  }, [sortBy, sortByDirection]);

  const handleSortModelChange = useCallback(
    (nextSortModel: GridSortModel) => {
      const nextSort = nextSortModel[0];
      setPromoPreferences((current) => ({
        ...current,
        uiSelection: {
          ...current.uiSelection,
          voucherList: { ...current.uiSelection.voucherList, sortModel: nextSortModel },
        },
      }));
      if (!nextSort?.sort) {
        onSortChange();
        return;
      }

      const nextSortBy = VOUCHER_LIST_SORT_FIELD_TO_API_SORT_BY[nextSort.field];
      if (nextSortBy) {
        onSortChange(nextSortBy, nextSort.sort === 'desc' ? 'desc' : 'asc');
      }
    },
    [onSortChange, setPromoPreferences],
  );

  useEffect(() => {
    if (hasRestoredSort.current || sortBy || !voucherListPreferences.sortModel.length) {
      return;
    }

    hasRestoredSort.current = true;
    handleSortModelChange(voucherListPreferences.sortModel);
  }, [handleSortModelChange, sortBy, voucherListPreferences.sortModel]);

  const handleColumnVisibilityModelChange = useCallback(
    (nextModel: GridColumnVisibilityModel) => {
      setColumnVisibilityModel(nextModel);
      setPromoPreferences((current) => ({
        ...current,
        uiSelection: {
          ...current.uiSelection,
          voucherList: { ...current.uiSelection.voucherList, columnVisibility: nextModel },
        },
      }));
    },
    [setPromoPreferences],
  );

  const handleDateSortClick = useCallback(
    (field: 'createdAt' | 'updatedAt') => {
      const currentSort = sortModel[0];
      if (currentSort?.field !== field || !currentSort.sort) {
        handleSortModelChange([{ field, sort: 'asc' }]);
        return;
      }
      if (currentSort.sort === 'asc') {
        handleSortModelChange([{ field, sort: 'desc' }]);
        return;
      }
      handleSortModelChange([]);
    },
    [handleSortModelChange, sortModel],
  );

  const handleOpenWarnings = useCallback((voucher: Voucher, severity: AlertColor) => {
    setWarningsDialog({ voucher, severity });
  }, []);

  const handleCloseWarnings = useCallback(() => {
    setWarningsDialog(undefined);
  }, []);

  const cancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setRowToDelete(undefined);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!rowToDelete) {
      return;
    }
    try {
      await onConfirmDelete(rowToDelete);
      cancelDelete();
    } catch (error) {
      // Keep dialog open if delete fails.
    }
  }, [cancelDelete, onConfirmDelete, rowToDelete]);

  const handleColumnVisibilityChange = useCallback((field: string, visible: boolean) => {
    setColumnVisibilityModel((prev) => ({ ...prev, [field]: visible }));
  }, []);

  const handleReportingTagsFilterChange = useCallback(
    (event: SelectChangeEvent<Array<string>>) => {
      const value = event.target.value;
      setSelectedReportingTags(typeof value === 'string' ? value.split(',') : value);
    },
    [setSelectedReportingTags],
  );

  const handleExcludedReportingTagsFilterChange = useCallback(
    (event: SelectChangeEvent<Array<string>>) => {
      const value = event.target.value;
      setExcludedReportingTags(typeof value === 'string' ? value.split(',') : value);
    },
    [setExcludedReportingTags],
  );

  const excludeReportingTag = useCallback(
    (tag: string) => {
      setSelectedReportingTags(selectedReportingTags.filter((selectedTag) => selectedTag !== tag));
      setExcludedReportingTags(
        excludedReportingTags.includes(tag) ? excludedReportingTags : [...excludedReportingTags, tag],
      );
    },
    [excludedReportingTags, selectedReportingTags, setExcludedReportingTags, setSelectedReportingTags],
  );

  const hasActiveFilters =
    Boolean(voucherName) ||
    selectedReportingTags.length > 0 ||
    excludedReportingTags.length > 0 ||
    Boolean(adminUserId) ||
    caseIdFilter.length > 0 ||
    !effectiveOnlyActive;

  const handleCaseIdFilterChange = useCallback(
    (_event: React.SyntheticEvent, newValue: ReadonlyArray<string>) => {
      const normalized = newValue
        .map((v) => normalizeCaseIdFilterToken(typeof v === 'string' ? v : ''))
        .filter(Boolean);
      setCaseIdFilter([...new Set(normalized)]);
      setCaseIdFilterInput('');
    },
    [setCaseIdFilter],
  );

  const flushPendingCaseIdFilterInput = useCallback(() => {
    setCaseIdFilterInput((input) => {
      const normalized = normalizeCaseIdFilterToken(input);
      if (!normalized) {
        return input;
      }
      const prev = caseIdFilterRef.current;
      setCaseIdFilter([...new Set([...prev, normalized])]);
      return '';
    });
  }, [setCaseIdFilter]);

  const standardColumns = useVoucherColumns({
    auditUsersById,
    onVoucherToggleClick,
    onDateSortClick: handleDateSortClick,
    selectedVoucherToggle,
  });
  const reportingTagsColumn = useMemo<GridColDef<Voucher>>(
    () => ({
      field: 'voucherReportingTags',
      headerName: 'Reporting Tags',
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      hideable: true,
      flex: 1,
      minWidth: 220,
      renderCell: (params) => {
        const tags = params.row.voucherReportingTags;

        if (!tags?.length) {
          return '—';
        }

        return (
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ py: 0.5 }}>
            {tags.map((tag) => {
              const label = reportingTagLabel(tag);
              const isSelected = selectedReportingTags.includes(tag);

              return (
                <ReportingTag
                  key={tag}
                  label={label}
                  dropdownContent={
                    isSelected ? (
                      <MenuItem onClick={() => excludeReportingTag(tag)}>Exclude {label}</MenuItem>
                    ) : undefined
                  }
                />
              );
            })}
          </Stack>
        );
      },
    }),
    [excludeReportingTag, reportingTagLabel, selectedReportingTags],
  );
  const actionColumns = useMemo(() => getColumns({ onOpenWarnings: handleOpenWarnings }), [handleOpenWarnings]);
  const columns = useMemo(
    () => [...standardColumns, reportingTagsColumn, ...actionColumns],
    [actionColumns, reportingTagsColumn, standardColumns],
  );
  const hiddenColumnCount = useMemo(
    () => columns.filter((column) => column.hideable !== false && columnVisibilityModel[column.field] === false).length,
    [columnVisibilityModel, columns],
  );
  const hasNonDefaultColumnVisibility = useMemo(
    () =>
      columns.some(
        (column) =>
          (columnVisibilityModel[column.field] !== false) !== (defaultColumnVisibilityModel[column.field] !== false),
      ),
    [columnVisibilityModel, columns, defaultColumnVisibilityModel],
  );
  const handleResetColumns = useCallback(() => {
    handleColumnVisibilityModelChange(defaultColumnVisibilityModel);
  }, [defaultColumnVisibilityModel, handleColumnVisibilityModelChange]);

  const warningsDialogList = warningsDialog
    ? getVoucherWarnings(warningsDialog.voucher).filter(
        (warning) => getVoucherWarningSeverity(warning.level) === warningsDialog.severity,
      )
    : [];
  const [recentVoucherNames, setRecentVoucherNames] = useState<Array<string>>(() =>
    getPromoRecentVisits('voucher').map(({ code }) => code),
  );

  useEffect(() => {
    const syncRecentVoucherNames = () => {
      setRecentVoucherNames(getPromoRecentVisits('voucher').map(({ code }) => code));
    };
    window.addEventListener(PROMO_RECENT_VISITS_UPDATED_EVENT, syncRecentVoucherNames);
    return () => window.removeEventListener(PROMO_RECENT_VISITS_UPDATED_EVENT, syncRecentVoucherNames);
  }, []);

  const Toolbar = useCallback(
    () => (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
        <ColumnSelector
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          size="small"
        />
        {hasNonDefaultColumnVisibility && (
          <Button size="small" variant="text" onClick={handleResetColumns}>
            Reset ({hiddenColumnCount} columns hidden)
          </Button>
        )}
      </Stack>
    ),
    [
      columnVisibilityModel,
      columns,
      handleColumnVisibilityChange,
      handleResetColumns,
      hasNonDefaultColumnVisibility,
      hiddenColumnCount,
    ],
  );

  return (
    <>
      <Grid container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid size={12} container spacing={2}>
          <Grid size={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Autocomplete
                freeSolo
                options={recentVoucherNames}
                groupBy={() => 'recent-visits'}
                inputValue={voucherName}
                onInputChange={(_, value) => setVoucherName(value)}
                onOpen={() => setRecentVoucherNames(getPromoRecentVisits('voucher').map(({ code }) => code))}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <Typography component="div" variant="caption" color="text.secondary" sx={{ px: 2, pt: 1 }}>
                      Recently visited
                    </Typography>
                    <Box component="ul" sx={{ m: 0, p: 0 }}>
                      {params.children}
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Voucher Name" size="small" />}
                fullWidth
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={effectiveOnlyActive}
                    onChange={(_, checked) => setOnlyActive(checked)}
                    disabled={caseIdFilterActive}
                    color="primary"
                  />
                }
                label="Active vouchers only"
              />
              <Button variant="text" onClick={() => setShowMoreFilters((visible) => !visible)}>
                {showMoreFilters ? 'Hide filters' : 'More filters'}
              </Button>
              {hasActiveFilters && (
                <Button variant="text" onClick={onClearAllFilters}>
                  Clear all filters
                </Button>
              )}
              {selectedVoucherToggle && (
                <Chip
                  label={`Toggle: ${selectedVoucherToggle} (loaded page)`}
                  size="small"
                  onDelete={onClearVoucherToggle}
                />
              )}
            </Stack>
            <Collapse in={showMoreFilters}>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="voucher-reporting-tags-filter-label">Reporting Tags</InputLabel>
                    <Select<Array<string>>
                      multiple
                      labelId="voucher-reporting-tags-filter-label"
                      value={selectedReportingTags}
                      onChange={handleReportingTagsFilterChange}
                      input={<OutlinedInput label="Reporting Tags" />}
                      renderValue={(selected) => (
                        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ py: 0.25 }}>
                          {selected.map((tag) => {
                            const reportingTagLabel =
                              reportingTagFilterOptions.find((option) => option.value === tag)?.label ?? tag;

                            return (
                              <ReportingTag
                                key={tag}
                                label={reportingTagLabel}
                                dropdownContent={
                                  <MenuItem onClick={() => excludeReportingTag(tag)}>
                                    Exclude {reportingTagLabel}
                                  </MenuItem>
                                }
                              />
                            );
                          })}
                        </Stack>
                      )}
                    >
                      {reportingTagFilterOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <ListItemText primary={option.label} secondary={option.value} />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Matches vouchers with any selected tag.</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl size="small" fullWidth sx={{ borderLeft: 3, borderColor: 'warning.main', pl: 1 }}>
                    <InputLabel id="voucher-reporting-tags-exclude-filter-label">Exclude reporting tags</InputLabel>
                    <Select<Array<string>>
                      multiple
                      labelId="voucher-reporting-tags-exclude-filter-label"
                      value={excludedReportingTags}
                      onChange={handleExcludedReportingTagsFilterChange}
                      input={<OutlinedInput label="Exclude reporting tags" />}
                      renderValue={(selected) =>
                        selected
                          .map((tag) => reportingTagFilterOptions.find((option) => option.value === tag)?.label ?? tag)
                          .join(', ')
                      }
                    >
                      {reportingTagFilterOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <ListItemText primary={option.label} secondary={option.value} />
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Excludes vouchers with any selected tag.</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <UserSearchWidget
                    id="voucher-audit-user-search"
                    label="Created or updated by"
                    formErrors={[]}
                    onChange={(_, userId) => setAdminUserId(userId)}
                    passedAutocompleteValue={adminUserSearchValue}
                    setPassedAutocompleteValue={setAdminUserSearchValue}
                  />
                  <FormHelperText>Matches either the voucher creator or most recent updater.</FormHelperText>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[...CASE_ID_FILTER_PRESET_OPTIONS]}
                    value={caseIdFilter}
                    inputValue={caseIdFilterInput}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === 'reset') {
                        setCaseIdFilterInput('');
                        return;
                      }
                      setCaseIdFilterInput(newInputValue);
                    }}
                    onChange={handleCaseIdFilterChange}
                    getOptionLabel={(option) => caseIdFilterOptionLabel(option)}
                    isOptionEqualToValue={(a, b) => a === b}
                    filterSelectedOptions
                    fullWidth
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Case ID"
                        placeholder="set, not_set, or exact ID(s)"
                        inputProps={{
                          ...params.inputProps,
                          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                            params.inputProps?.onBlur?.(e);
                            const next = e.relatedTarget instanceof Node ? e.relatedTarget : null;
                            if (next?.closest?.('[role="listbox"]')) {
                              return;
                            }
                            flushPendingCaseIdFilterInput();
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </Grid>
        <Grid size={4} container spacing={2}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" width="100%">
            <Button variant="contained" color="primary" component={Link} to="/marketing/vouchers-create">
              New Voucher
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination, toolbar: Toolbar }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={rows}
          columns={columns}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          sortingMode="server"
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.voucherId}
          getRowHeight={() => 'auto'}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
        />
      </Box>
      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Delete Voucher</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete voucher {rowToDelete?.voucherName || rowToDelete?.voucherId}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={cancelDelete} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleConfirmDelete} disabled={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(warningsDialog)} onClose={handleCloseWarnings} maxWidth="sm" fullWidth>
        <DialogTitle>
          Voucher {warningsDialog ? getVoucherWarningSeverityLabel(warningsDialog.severity).toLowerCase() : 'warning'}
          {warningsDialog ? ` — ${warningsDialog.voucher.voucherName}` : ''}
        </DialogTitle>
        <DialogContent>
          {warningsDialogList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No {warningsDialog ? getVoucherWarningSeverityLabel(warningsDialog.severity).toLowerCase() : 'warning'}{' '}
              entries for this voucher.
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {warningsDialogList.map((warning, index) => (
                <Alert
                  key={`${warning.code ?? 'warning'}-${index}`}
                  severity={getVoucherWarningSeverity(warning.level)}
                >
                  <AlertTitle>{warning.code ?? 'Voucher warning'}</AlertTitle>
                  {warning.message}
                </Alert>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCloseWarnings}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
